import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { UtilsService } from '../utils/utils.service';
import { URLS_CONST } from 'src/app/shared/constants/url-constants';
import { Observable, throwError, of } from 'rxjs';
import * as _ from 'lodash';
import { map, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  constructor(public _utilsService: UtilsService) { }

  /**
   * Gets SMS templates for a brand
   * @returns Observable with SMS templates
   */
  getSmsTemplates(): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/sms_templates');
    return this._utilsService.getAPICall(url, this._utilsService.setHeaders());
  }

  /**
   * Updates brand configuration
   * @param brandId - The brand ID
   * @param configuration - Configuration object to update
   * @returns Observable with response data
   */
  updateBrandConfiguration(brandId: string, configuration: any): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/settings');
    const params: any = {
      [URLS_CONST.BRAND_ID]: brandId
    };
    return this._utilsService.putAPICall(url, configuration, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Updates rating messages for a brand
   * @param brandId - The brand ID
   * @param ratingMessages - Rating messages to update
   * @returns Observable with response data
   */
  updateRatingMessages(brandId: string, ratingMessages: any): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/save_rating_messages');
    const params: any = {
      [URLS_CONST.BRAND_ID]: brandId
    };
    const requestBody = {
      rating_messages: ratingMessages
    };
    return this._utilsService.putAPICall(url, requestBody, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Gets rating parameters based on provided params
   * @param params - Parameters for the API call (brand_id, shop_id_list, etc.)
   * @returns Observable with response data
   */
  getRatingParameters(params: any): Observable<any> {
    let paramsClone = _.clone(params);
    let url = '/configs/detailed_feedback_items';

    // Handle shop_id_list special case (same logic as old service)
    if (paramsClone.shop_id_list && paramsClone.shop_id_list.length > 0) {
      delete paramsClone['brand_id'];
      delete paramsClone['group_id'];
    } else {
      delete paramsClone['shop_id_list'];
    }

    const apiUrl = this._utilsService.apiUrlGeneration(environment.webPrivateV1, url);
    return this._utilsService.getAPICall(apiUrl, this._utilsService.setHeaderWithParams(paramsClone));
  }

  /**
   * Gets module configuration based on provided params
   * @param params - Parameters for the API call
   * @returns Observable with response data
   */
  getModuleConfiguration(params: any): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/configs/modules');
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Inserts new field definitions for a brand (equivalent to Brands.insertFieldDefinations)
   * @param brandId - The brand ID
   * @param fieldDefinitions - Field definitions array to insert
   * @returns Observable with response data
   */
  insertFieldDefinitions(brandId: string, fieldDefinitions: any[]): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.updateFieldDefinitions);
      const requestBody = {
        brand_id: brandId,
        field_definitions: fieldDefinitions
      };
      return this._utilsService.postAPICall(url, requestBody, this._utilsService.setHeaders());
    } catch (error) {
      console.error('Error in insertFieldDefinitions:', error);
      return throwError('Error inserting field definitions');
    }
  }

  /**
   * Updates field definitions for a brand (equivalent to Brands.updateFieldDefinations)
   * @param brandId - The brand ID
   * @param fieldDefinitions - Field definitions array to update
   * @param type - Rule mode (null for group updates, matching old dashboard)
   * @param rule - Rule data (null for group updates, matching old dashboard)
   * @returns Observable with response data
   */
  updateFieldDefinition(brandId: string, fieldDefinitions: any[], type: string | null = null, rule: any = null): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.updateFieldDefinitions);
    const params: any = {
      [URLS_CONST.BRAND_ID]: brandId
    };
    const requestBody: any = {
      field_definitions: fieldDefinitions
    };
    // Add ruleMode and rule if provided (for rule updates, not group updates)
    if (type !== null) {
      requestBody.ruleMode = type;
    }
    if (rule !== null) {
      requestBody.rule = rule;
    }
    return this._utilsService.putAPICall(url, requestBody, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Removes field definitions for a brand (equivalent to Brands.removeFieldDefinations)
   * @param brandId - The brand ID
   * @param fieldId - The field ID to delete
   * @param type - Type of deletion: 'deleteParent' or 'deleteChild'
   * @param fieldIds - Array of all field IDs to delete (includes parent and all children for parent deletion)
   * @returns Observable with response data
   */
  removeFieldDefinitions(brandId: string, fieldId: string, type: string, fieldIds: string[]): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.updateFieldDefinitions);
      // Build query params matching old dashboard: field_id, type, field_ids[], brand_id
      // Old dashboard uses field_ids[] (array notation) for each field ID
      const queryParams: string[] = [];
      queryParams.push(`field_id=${encodeURIComponent(fieldId)}`);
      queryParams.push(`type=${encodeURIComponent(type)}`);
      // Add each field_id as field_ids[] parameter (matching old dashboard format)
      fieldIds.forEach(id => {
        queryParams.push(`field_ids[]=${encodeURIComponent(id)}`);
      });
      queryParams.push(`brand_id=${encodeURIComponent(brandId)}`);

      // Build URL with query params
      const queryString = queryParams.join('&');
      const urlWithParams = `${url}?${queryString}`;
      return this._utilsService.deleteAPICall(urlWithParams, {}, this._utilsService.setHeaders());
    } catch (error) {
      console.error('Error in removeFieldDefinitions:', error);
      return throwError('Error removing field definitions');
    }
  }

  /**
   * Gets feedback configuration for a brand
   * @param brandId - The brand ID
   * @returns Observable with response data
   */
  getFeedbackConfiguration(brandId: string): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/configs/configuration');
    const params: any = {
      [URLS_CONST.BRAND_ID]: brandId
    };
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(params));
  }

  /**
 * Gets active category list for a brand
 * @param brandId - The brand ID
 * @returns Observable with response data
 */
  getCategoryList(brandId: string): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/categories/list');
    const params: any = {
      [URLS_CONST.BRAND_ID]: brandId
    };
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Creates a new category
   * @param categoryData - category details
   * @returns Observable with response data
  */
  createCategory(categoryData: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.creteCategory);
      return this._utilsService.postAPICall(url, categoryData, this._utilsService.setHeaders());
    } catch (error) {
      console.error('Error in createLitmusJob:', error);
      return throwError('Error creating Litmus job');
    }
  }

  /**
   * Updates category details
   * @param brandId - The brand ID
   * @param categoryData - categoryData to update
   * @returns Observable with response data
   */
  updateCategory(brandId: string, categoryData: any): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.updateCategory);
    const params: any = {
      [URLS_CONST.BRAND_ID]: brandId
    };
    return this._utilsService.putAPICall(url, categoryData, this._utilsService.setHeaderWithParams(params));
  }


  /**
   * Delete Category Details
   * @param categoryData - category data for deletion
   * @returns Observable with response data
  */
  deleteCategory(categoryData: any): Observable<any> {
    // API endpoint: /categories/remove_delete
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/categories/remove');

    // Determine level if not provided: 0 for parent category, 1 for sub-category
    const level = categoryData["level"] !== undefined
      ? categoryData["level"]
      : (categoryData["parent_id"] ? '1' : '0');

    const params: any = {
      [URLS_CONST.BRAND_ID]: categoryData["brandId"],
      [URLS_CONST.CATEGORY_ID]: categoryData["uuid"],
      [URLS_CONST.LEVEL]: level
    };

    // parent_id is only required when level is 1
    if (level === '1' || level === 1) {
      if (categoryData["parent_id"]) {
        params[URLS_CONST.PARENT_ID] = categoryData["parent_id"];
      }
    }

    return this._utilsService.deleteAPICall(url, {}, this._utilsService.setHeaderWithParams(params));
  }

  updateCategoryRuleBasedOnType(type: any, brandId: any, updated_list: any) {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.categoryRuleBasedOnType);
    const params: any = {
      brand_id: brandId
    };
    const requestBody: any = {
      "type": type,
      "updated_list": updated_list
    };
    return this._utilsService.putAPICall(url, requestBody, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Gets feedback configuration by version for a brand
   * @param brandId - The brand ID
   * @param version - The version to retrieve
   * @returns Observable with response data
   */
  getFeedbackConfigurationByVersion(brandId: string, version: string): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/configs/configuration');
    const params: any = {
      [URLS_CONST.BRAND_ID]: brandId,
      version: version
    };
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Updates questionnaire for a brand
   * @param brandId - The brand ID
   * @param configuration - Configuration to update
   * @param isPublish - Whether to publish the configuration
   * @returns Observable with response data
   */
  updateQuestionnaire(brandId: string, configuration: any, isPublish: boolean): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/brands/configuration');

      // Create a deep copy and remove _id as in the original
      const newConfiguration = { ...configuration };
      if (newConfiguration._id) {
        delete newConfiguration._id;
      }

      const requestBody = {
        brand_id: brandId,
        brand_config: newConfiguration,
        is_publish: isPublish
      };

      return this._utilsService.postAPICall(url, requestBody, this._utilsService.setHeaders());
    } catch (error) {
      console.error('Error in updateQuestionnaire:', error);
      return throwError('Error updating questionnaire');
    }
  }

  /**
   * Gets help resource by brand ID
   * @param brandId - The brand ID
   * @returns Observable with response data
   */
  getHelpResourceByBrandId(brandId: string): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/help/by_brand_id');
    const params: any = {
      [URLS_CONST.BRAND_ID]: brandId
    };
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Gets questionnaire components
   * @returns Observable with response data
   */
  getQuestionnaireComponents(): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/get_all_components');
    const params: any = {
      is_latest: true
    };
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Gets component schema by component ID
   * @param componentId - The component ID
   * @returns Observable with response data
   */
  getComponentSchema(componentId: string): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/get_component_schema');
    const params: any = {
      component_id: componentId
    };
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Gets feedback URL for a request
   * @param requestObj - Request object
   * @returns Observable with response data
   */
  getFeedbackUrl(requestObj: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/feedbackrequests/generate_customer_feedback_url_v2');
      return this._utilsService.postAPICall(url, requestObj, this._utilsService.setHeaders());
    } catch (error) {
      console.error('Error in getFeedbackUrl:', error);
      return throwError('Error getting feedback URL');
    }
  }

  /**
   * Gets module configuration
   * @returns Observable with response data
   */
  getModuleConfig(): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/get_module_config');
    return this._utilsService.getAPICall(url, this._utilsService.setHeaders());
  }

  /**
   * Gets V2 module configuration
   * @param params - Parameters for the API call
   * @returns Observable with response data
   */
  getV2ModuleConfig(params: any): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/configs/module_config_v2');
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Gets default V2 configuration
   * @returns Observable with response data
   */
  getDefaultV2Config(): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/configs/default_config_v2');
    return this._utilsService.getAPICall(url, this._utilsService.setHeaders());
  }

  /**
   * Gets setting configuration by field key
   * @param fieldKey - The field key
   * @returns Observable with response data
   */
  getSettingConfig(fieldKey: string): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/configs/settings');
    const params: any = {
      field_key: fieldKey
    };
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Creates a new Litmus job
   * @param requestData - Job request data
   * @returns Observable with response data
   */
  createLitmusJob(requestData: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/jobs/create');
      return this._utilsService.postAPICall(url, requestData, this._utilsService.setHeaders());
    } catch (error) {
      console.error('Error in createLitmusJob:', error);
      return throwError('Error creating Litmus job');
    }
  }

  /**
   * Requests for Litmus jobs 
   * @param requestData - Request data for jobs
   * @returns Observable with response data
   */
  requestForLitmusJobs(requestData: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.createJobRequest);
      return this._utilsService.postAPICall(url, requestData, this._utilsService.setHeaders());
    } catch (error) {
      console.error('Error in requestForLitmusJobs:', error);
      return throwError('Error requesting Litmus jobs');
    }
  }


  /**
   * Updates brand module configuration
   * @param brandId - The brand ID
   * @param configuration - Configuration to update
   * @returns Observable with response data
   */
  updateBrandModuleConfiguration(brandId: string, configuration: any): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/brands/module_configurations');
    const params: any = {
      [URLS_CONST.BRAND_ID]: brandId
    };
    return this._utilsService.putAPICall(url, configuration, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Gets access control report
   * @param requestData - Request data for the report
   * @returns Observable with response data
   */
  getAccessControlReport(requestData: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/dashboard/access_control/summary');
      return this._utilsService.postAPICall(url, requestData, this._utilsService.setHeaders());
    } catch (error) {
      console.error('Error in getAccessControlReport:', error);
      return throwError('Error getting access control report');
    }
  }

  /**
   * Updates project information
   * @param brandId - The brand ID
   * @param configuration - Configuration to update
   * @returns Observable with response data
   */
  updateProjectInfo(brandId: string, configuration: any): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/brands/projects/info');
    const params: any = {
      [URLS_CONST.BRAND_ID]: brandId
    };
    return this._utilsService.putAPICall(url, configuration, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Deletes AFP configuration
   * @param requestData - Request data for deletion
   * @returns Observable with response data
   */
  deleteAFPConfig(requestData: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/brands/afp/delete');
      return this._utilsService.postAPICall(url, requestData, this._utilsService.setHeaders());
    } catch (error) {
      console.error('Error in deleteAFPConfig:', error);
      return throwError('Error deleting AFP configuration');
    }
  }

  /**
  * Deletes AFP configuration
  * @param requestData - an object containing config_name/brand_id/type for config to delete
  */
  deleteMappingAFPConfig(requestData: any): Observable<any> {
    // API endpoint same as create/get configs
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/configs/afp_configs');
    // Uses deleteAPICall which issues DELETE with requestData as body
    return this._utilsService.deleteAPICall(url, {}, this._utilsService.setHeaderWithParams(requestData));
  }
  /**
   * Gets elastic configuration
   * @returns Observable with response data
   */
  getElasticConfig(): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/get_elastic_config');
    return this._utilsService.getAPICall(url, this._utilsService.setHeaders());
  }

  /**
   * Updates brand dashboard configuration
   * @param brandId - The brand ID
   * @param configuration - Configuration to update
   * @param isCacheDataDelete - Whether to delete cache data
   * @returns Observable with response data
   */
  updateBrandDashboardConfiguration(brandId: string, configuration: any, isCacheDataDelete: boolean = true): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.updateBrandConfiguration);
    const params: any = {
      [URLS_CONST.BRAND_ID]: brandId,
      is_cache_delete_flag: isCacheDataDelete
    };
    return this._utilsService.putAPICall(url, configuration, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Gets field definations for inbox details
   * @param brandId - The brand ID
   * @param source - inbox_detail
   * @param type - 1
   * @returns Observable with response data
   */
  getFieldDefinitions(params: any) {//fied definations 
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.getFieldDefinitions);
      return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(params));
    } catch (error) {
      console.error('An unexpected error occurred in getFieldDefinitions:', error);
      return throwError('An unexpected error occurred in getFieldDefinitions');
    }
  }
  /**
   * PUT `configs/dashboard_configuration?brand_id=` — Litmus `dashboard_configuration_put.js`
   * (e.g. `module_configuration_v2`, `field_definitions`).
   */
  saveConfigsDashboardConfiguration(brandId: string, body: Record<string, unknown>): Observable<any> {
    const wp: any = environment.webPrivateV1;
    const path = wp.dashboardConfiguration || '/configs/dashboard_configuration';
    const url = this._utilsService.apiUrlGeneration(wp, path);
    const params: Record<string, string> = { [URLS_CONST.BRAND_ID]: brandId };
    return this._utilsService.putAPICall(url, body, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Adds reporting configuration
   * @param brandId - The brand ID
   * @param requestData - Request data for the configuration
   * @returns Observable with response data
   */
  addReportingConfig(brandId: string, requestData: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/brands/reporting_configurations');
      const finalRequest = {
        ...requestData,
        [URLS_CONST.BRAND_ID]: brandId
      };
      return this._utilsService.postAPICall(url, finalRequest, this._utilsService.setHeaders());
    } catch (error) {
      console.error('Error in addReportingConfig:', error);
      return throwError('Error adding reporting configuration');
    }
  }

  /**
   * Updates reporting configuration
   * @param brandId - The brand ID
   * @param requestData - Request data for the configuration
   * @returns Observable with response data
   */
  updateReportingConfig(brandId: string, requestData: any): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/brands/reporting_configurations');
    const params: any = {
      [URLS_CONST.BRAND_ID]: brandId
    };
    return this._utilsService.putAPICall(url, requestData, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Creates a missed call
   * @param requestData - Request data for the missed call
   * @returns Observable with response data
   */
  createMissedCall(requestData: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/missedcalls/create');
      return this._utilsService.postAPICall(url, requestData, this._utilsService.setHeaders());
    } catch (error) {
      console.error('Error in createMissedCall:', error);
      return throwError('Error creating missed call');
    }
  }

  /**
   * Resets password
   * @param resetRequestObj - Reset request object
   * @returns Observable with response data
   */
  resetPassword(resetRequestObj: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/managers/reset_password');
      return this._utilsService.postAPICall(url, resetRequestObj, this._utilsService.setHeaders());
    } catch (error) {
      console.error('Error in resetPassword:', error);
      return throwError('Error resetting password');
    }
  }

  /**
   * Updates role-based configuration
   * @param params - Parameters for the update
   * @returns Observable with response data
   */
  updateRoleBasedConfig(params: any): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/managers/update_role_based_config');
    return this._utilsService.postAPICall(url, params, this._utilsService.setHeaders());
  }

  /**
   * Gets module configuration by brand
   * @param query - Query parameters
   * @returns Observable with response data
   */
  getModuleConfigByBrand(query: any): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/get_module_config_by_brand');
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(query));
  }

  /**
   * Creates client SSO configuration
   * @param accountId - The account ID
   * @param configuration - Configuration to create
   * @returns Observable with response data
   */
  createClientSSOConfiguration(accountId: string, configuration: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/accounts/sso_configurations');
      const finalRequest = {
        ...configuration,
        [URLS_CONST.ACCOUNT_ID]: accountId
      };
      return this._utilsService.postAPICall(url, finalRequest, this._utilsService.setHeaders());
    } catch (error) {
      console.error('Error in createClientSSOConfiguration:', error);
      return throwError('Error creating client SSO configuration');
    }
  }

  getManagerListsOrganisation(reqBody: any): any {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.organisationManagerList);
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(reqBody));
  }

  getManagerLists(type: string, id: string): any {
    var queryParameter:any = {};
    if (type == "brand") {
      queryParameter = {
        brand_id: id,
        manager_type: 2
      };
    } else if (type == "managers") {
      queryParameter = {
        manager_id_list: id
      };
    }
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.managersList);
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(queryParameter));
  }

  getLevelListsOrganisation(reqBody: any): any {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.organisationLevelLists);
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(reqBody));
  }

  updateManagerOrganisation(reqBody: any, brandId: string): any {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.organisationManagerUpdate);
    return this._utilsService.putAPICall(url, reqBody, this._utilsService.setHeaderWithParams({ brand_id: brandId }));
  }

  updateManagerListOrganisation(reqBody: any): any {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.organisationManagerListUpdate);
    return this._utilsService.postAPICall(url, reqBody, this._utilsService.setHeaders());
  }

  getDynamicHierarchyList(reqBody: any): any {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.dyanamicHierarchyList);
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(reqBody));
  }

  registerManagerProfile(reqBody: any): any {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.registerManagerProfile);
    return this._utilsService.postAPICall(url, reqBody, this._utilsService.setHeaders());
  }

  sendVerificationMail(reqBody: any): any {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.sendVerificationMail);
    return this._utilsService.postAPICall(url, reqBody, this._utilsService.setHeaders());
  }

  searchManager(reqBody: any): any {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.organisationSearchManager);
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(reqBody));
  }

  /**
   * Email notification templates by brand (matching Brands.getTemplatesByBrandId)
   */
  getEmailTemplatesByBrandId(brandId: string): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/notification_configuration/template');
    const params: any = {
      [URLS_CONST.BRAND_ID]: brandId
    };
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Upload email HTML template (matching DeliveryChannelConfiguration.uploadTemplate)
   */
  uploadEmailTemplate(brandId: string, managerId: string, file: File): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/notification_configuration/template');
    // Do not set Content-Type so browser can send multipart boundary
    return this._utilsService.upload(url, {
      file,
      fileFormDataName: 'file',
      brand_id: brandId,
      manager_id: managerId
    }, {});
  }

  /**
   * Preview/download email template content by file id
   */
  getEmailTemplatePreview(fileId: string): Observable<any> {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/notification_configuration/template');
    const params: any = {
      file_id: fileId
    };
    return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(params));
  }

  /**
   * Gets workflow rules configuration (fields and operators) for category rule creation
   * @param brandId - The brand ID
   * @returns Observable with response data containing fields and operators
   */
  getWorkflowRules(brandId: string, type): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/actions/workflow_rules');
      const params: any = {
        [URLS_CONST.BRAND_ID]: brandId,
        [URLS_CONST.TYPE]: type
      };
      return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(params));
    } catch (error) {
      console.error('Error in getWorkflowRules:', error);
      return throwError('Error getting workflow rules');
    }
  }

  /**
   * Gets status list by brand ID (equivalent to Feeds.getStatusByBrandId)
   * @param brandId - The brand ID
   * @returns Observable with response data containing status_list and managers
   */
  getStatusByBrandId(brandId: string): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.brandsStatusList);
      const params: any = {
        [URLS_CONST.BRAND_ID]: brandId
      };
      return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(params));
    } catch (error) {
      console.error('Error in getStatusByBrandId:', error);
      return throwError('Error getting status list');
    }
  }

  /**
   * Updates a single status (equivalent to Issues.updateStatus)
   * @param brandId - The brand ID
   * @param statusDetail - The status details to update
   * @returns Observable with response data
   */
  updateStatus(brandId: string, statusDetail: any): Observable<any> {
    try {
      // Match old AngularJS request body structure: { brand_id, status_details }
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/issues/status');
      const params: any = {
        [URLS_CONST.BRAND_ID]: brandId
      };
      const requestBody = {
        [URLS_CONST.BRAND_ID]: brandId,
        status_details: statusDetail // Old service wraps single status in status_details array, but sends as object
      };
      return this._utilsService.putAPICall(url, requestBody, this._utilsService.setHeaderWithParams(params));
    } catch (error) {
      console.error('Error in updateStatus:', error);
      return throwError('Error updating status');
    }
  }

  /**
   * Adds new status(es) by brand ID (equivalent to Issues.addStatusByBrandId)
   * @param brandId - The brand ID
   * @param statusDetails - Array of status details to add
   * @returns Observable with response data
   */
  addStatusByBrandId(brandId: string, statusDetails: any[]): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/issues/status');
      const params: any = {
        [URLS_CONST.BRAND_ID]: brandId
      };
      const requestBody = {
        status_details: statusDetails,
        brand_id: brandId
      };
      return this._utilsService.postAPICall(url, requestBody, this._utilsService.setHeaders());
    } catch (error) {
      console.error('Error in addStatusByBrandId:', error);
      return throwError('Error adding status');
    }
  }

  /**
   * Updates rules based on type (equivalent to Issues.updateRuleBasedOnType)
   * @param type - The type (1 for status, 2 for category)
   * @param brandId - The brand ID
   * @param updated_list - Array of items (statuses/categories) with rules to update
   * @returns Observable with response data
   */
  updateRuleBasedOnType(type: number, brandId: string, updated_list: any[]): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/actions/issue_filters');
      const params: any = {
        [URLS_CONST.BRAND_ID]: brandId
      };
      const requestBody = {
        updated_list: updated_list,
        type: type
      };
      return this._utilsService.putAPICall(url, requestBody, this._utilsService.setHeaderWithParams(params));
    } catch (error) {
      console.error('Error in updateRuleBasedOnType:', error);
      return throwError('Error updating rules');
    }
  }

  /**
   * Gets workflow list for a brand (equivalent to Workflows.getWorkflowList)
   * @param brandId - The brand ID
   * @returns Observable with response data containing rules and managers
   */
  getWorkflowList(brandId: string): Observable<any> {
    try {
      // Matching old AngularJS: api/actions/workflow_list
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/actions/workflow_list');
      const params: any = {
        [URLS_CONST.BRAND_ID]: brandId
      };
      return this._utilsService.getAPICall(url, this._utilsService.setHeaderWithParams(params));
    } catch (error) {
      console.error('Error in getWorkflowList:', error);
      return throwError('Error getting workflow list');
    }
  }

  /**
   * Updates a workflow (equivalent to Workflows.updateWorkflow)
   * @param param - Object containing brandId and workflow
   * @returns Observable with response data
   */
  updateWorkflow(param: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/actions/workflow_rule');
      const params: any = {
        [URLS_CONST.BRAND_ID]: param.brandId
      };
      if(param.workflow && param.workflow.uuid && param.workflow.uuid !== '' && param.workflow.uuid !== undefined && param.workflow.uuid !== null) {
        params.uuid = param.workflow.uuid;
      }
      return this._utilsService.putAPICall(url, param.workflow, this._utilsService.setHeaderWithParams(params));
    } catch (error) {
      console.error('Error in updateWorkflow:', error);
      return throwError('Error updating workflow');
    }
  }

  /**
   * Inserts a new workflow (equivalent to Workflows.insertWorkflow)
   * @param workflow - The workflow object to insert
   * @returns Observable with response data
   */
  insertWorkflow(workflow: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/actions/workflow_rule');
      return this._utilsService.postAPICall(url, workflow, this._utilsService.setHeaders());
    } catch (error) {
      console.error('Error in insertWorkflow:', error);
      return throwError('Error inserting workflow');
    }
  }

  /**
   * Deletes a workflow (equivalent to Workflows.deleteWorkflow)
   * @param param - Object containing brandId and uuid
   * @returns Observable with response data
   */
  deleteWorkflow(param: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, '/actions/workflow_rule');
      const params: any = {
        [URLS_CONST.BRAND_ID]: param.brandId,
        uuid: param.uuid
      };
      return this._utilsService.deleteAPICall(url, {}, this._utilsService.setHeaderWithParams(params));
    } catch (error) {
      console.error('Error in deleteWorkflow:', error);
      return throwError('Error deleting workflow');
    }
  }


  updateAccodionsList(brandId: string, accordions_list: any, accordion_rule: any, type: any, ruleType: any  ): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.staticAccordions);
      const params: any = {
        brand_id: brandId
      };
      var requestBody = {
        "accordions_list": accordions_list,
        "rule_type": ruleType,
        "accordion_rule": accordion_rule,
        "type": type
    }
      return this._utilsService.putAPICall(url, requestBody, this._utilsService.setHeaderWithParams(params));
    } catch (error) {
      console.error('Error in updateAccodionsList:', error);
      return throwError('Error updating accordions list');
    }
  }

  insertUIRules(brandId: string, ui_rules: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.uiRules);
      var requestBody = {
        brand_id: brandId,
        ui_rules: ui_rules
      };
      return this._utilsService.postAPICall(url, requestBody, this._utilsService.setHeaders());
    } catch (error) {
      console.error('Error in insertUIRules:', error);
      return throwError('Error inserting UI rules');
    }
  }

  updateUIRules(brandId: string, ui_rules: any, ruleDeleteFlag: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.uiRules);
      const params: any = {
        brand_id: brandId
      };
      var requestBody = {
        ui_rules: ui_rules,
        rule_remove_flag: ruleDeleteFlag
      };
      return this._utilsService.putAPICall(url, requestBody, this._utilsService.setHeaderWithParams(params));
    } catch (error) {
      console.error('Error in updateUIRules:', error);
      return throwError('Error updating UI rules');
    }
  }

  /**
 * Retrieves the list of dataset values from the server.
 * @param payload - The request parameters to be sent with the API call.
 * @returns Observable containing the dataset list response.
 */
  getDatasetList(payload: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(
        environment.webPrivateCs,
        environment.webPrivateCs.getDatasetValue
      );

      return this._utilsService.getAPICall(
        url,
        this._utilsService.setHeaderWithParams(payload)
      );

    } catch (error) {
      console.error('Error in getDatasetList:', error);
      return throwError(() => new Error('Error fetching dataset list'));
    }
  }


  /**
 * Uploads the dataset value set to the server.
 * @param params - The payload containing dataset value information.
 * @returns Observable containing the API response.
 */
  uploadDatasetValueSet(params: any): Observable<any> {
    try {
      const url = this._utilsService.apiUrlGeneration(
        environment.webPrivateCs,
        environment.webPrivateCs.uploadDatasetValue
      );

      return this._utilsService.postAPICall(
        url,
        params,
        this._utilsService.setHeaders()
      );

    } catch (error) {
      console.error('Error in uploadDatasetValueSet:', error);
      return throwError(() => new Error('Error uploading dataset value set'));
    }
  }

  managersBulkUpdate(reqBody: any): any {
    const url = this._utilsService.apiUrlGeneration(environment.webPrivateV1, environment.webPrivateV1.bulkUpdate);
    return this._utilsService.postAPICall(url, reqBody, this._utilsService.setHeaders());
  }
} 